import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    TextField,
    Grid,
    Paper,
    IconButton,
    Stack,
    Typography,
    useTheme,
    Fade,
} from "@mui/material";

import {
    CalendarMonth as CalendarIcon,
    Close as CloseIcon,
    ChevronLeft,
    ChevronRight,
} from "@mui/icons-material";

import Calendar from "./Calendar";
import DurationPreview from "./DurationPreview";

import {
    parseLocalDate,
    formatDate,
    formatDateToYYYYMMDD,
} from "./utils";

const DateRangePicker = ({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    error,
    helperText,
    required = false,
    disabled = false,
    isArabic = false,
    monthsToShow = 2,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [isOpen, setIsOpen] = useState(false);
    const [activeInput, setActiveInput] = useState("start");
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [hoverDate, setHoverDate] = useState(null);

    const wrapperRef = useRef(null);

    // close on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target)
            ) {
                setIsOpen(false);
                setHoverDate(null);
            }
        };

        document.addEventListener("mousedown", handleClick);

        return () =>
            document.removeEventListener(
                "mousedown",
                handleClick
            );
    }, []);

    const handleOpen = (type) => {
        if (disabled) return;
        setActiveInput(type);
        setIsOpen(true);

        if (type === "start" && startDate) {
            setCurrentMonth(
                parseLocalDate(startDate) || new Date()
            );
        }

        if (type === "end" && endDate) {
            setCurrentMonth(
                parseLocalDate(endDate) || new Date()
            );
        }
    };

    const handleDateClick = (date) => {
        const dateStr = formatDateToYYYYMMDD(date);

        if (activeInput === "start") {
            onStartDateChange(dateStr);

            if (
                endDate &&
                dateStr > endDate
            ) {
                onEndDateChange("");
            }

            setActiveInput("end");
        } else {
            if (!startDate) {
                onStartDateChange(dateStr);
                setActiveInput("end");
                return;
            }

            if (dateStr < startDate) {
                onEndDateChange(startDate);
                onStartDateChange(dateStr);
            } else {
                onEndDateChange(dateStr);
            }

            setTimeout(() => {
                setIsOpen(false);
            }, 150);
        }
    };

    const handlePrevMonth = () => {
        setCurrentMonth(
            new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth() - 1,
                1
            )
        );
    };

    const handleNextMonth = () => {
        setCurrentMonth(
            new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth() + 1,
                1
            )
        );
    };

    const clearStart = (e) => {
        e.stopPropagation();
        onStartDateChange("");
        onEndDateChange("");
    };

    const clearEnd = (e) => {
        e.stopPropagation();
        onEndDateChange("");
    };

    return (
        <Box ref={wrapperRef} sx={{ position: "relative" }}>
            {/* Inputs */}
            <Grid container spacing={2}>
                {/* Start */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        label={
                            isArabic
                                ? "تاريخ البداية"
                                : "Start Date"
                        }
                        value={formatDate(
                            startDate,
                            isArabic
                        )}
                        onClick={() =>
                            handleOpen("start")
                        }
                        error={error}
                        helperText={
                            activeInput === "start"
                                ? helperText
                                : ""
                        }
                        InputProps={{
                            readOnly: true,
                            startAdornment: (
                                <CalendarIcon
                                    sx={{
                                        mr: 1,
                                        color:
                                            isOpen &&
                                                activeInput ===
                                                "start"
                                                ? "primary.main"
                                                : "action.active",
                                    }}
                                />
                            ),
                            endAdornment: startDate && (
                                <IconButton
                                    size="small"
                                    onClick={clearStart}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            ),
                        }}
                    />
                </Grid>

                {/* End */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        label={
                            isArabic
                                ? "تاريخ النهاية"
                                : "End Date"
                        }
                        value={formatDate(
                            endDate,
                            isArabic
                        )}
                        onClick={() =>
                            handleOpen("end")
                        }
                        disabled={!startDate}
                        error={error}
                        helperText={
                            activeInput === "end"
                                ? helperText
                                : ""
                        }
                        InputProps={{
                            readOnly: true,
                            startAdornment: (
                                <CalendarIcon
                                    sx={{
                                        mr: 1,
                                        color:
                                            isOpen &&
                                                activeInput ===
                                                "end"
                                                ? "secondary.main"
                                                : "action.active",
                                    }}
                                />
                            ),
                            endAdornment: endDate && (
                                <IconButton
                                    size="small"
                                    onClick={clearEnd}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            ),
                        }}
                    />
                </Grid>
            </Grid>

            {/* Duration */}
            <DurationPreview
                startDate={startDate}
                endDate={endDate}
                theme={theme}
                isArabic={isArabic}
            />

            {/* Calendar */}
            {isOpen && (
                <Fade in>
                    <Paper
                        elevation={10}
                        sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: 3,
                            bgcolor: isDark
                                ? "#1e293b"
                                : "#fff",
                        }}
                    >
                        {/* Header */}
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mb: 2 }}
                        >
                            <IconButton
                                onClick={handlePrevMonth}
                            >
                                <ChevronLeft />
                            </IconButton>

                            <Typography fontWeight={700}>
                                {currentMonth.toLocaleDateString(
                                    isArabic
                                        ? "ar-SA"
                                        : "en-US",
                                    {
                                        month: "long",
                                        year: "numeric",
                                    }
                                )}
                            </Typography>

                            <IconButton
                                onClick={handleNextMonth}
                            >
                                <ChevronRight />
                            </IconButton>
                        </Stack>

                        {/* Calendar */}
                        <Calendar
                            currentMonth={
                                currentMonth
                            }
                            monthsToShow={
                                monthsToShow
                            }
                            startDate={startDate}
                            endDate={endDate}
                            hoverDate={hoverDate}
                            activeInput={activeInput}
                            onDateClick={
                                handleDateClick
                            }
                            onHover={setHoverDate}
                            theme={theme}
                            isArabic={isArabic}
                        />
                    </Paper>
                </Fade>
            )}
        </Box>
    );
};

export default DateRangePicker;